<template>
  <div class="signup-step active verify-email-step">
    <span class="eyebrow">{{ t('verify_email_eyebrow') }}</span>
    <h2>
      {{ t('verify_email_title_prefix')
      }}<span class="grad">{{ t('verify_email_title_grad') }}</span
      >{{ t('verify_email_title_suffix') }}
    </h2>
    <p class="sub">{{ t('verify_email_body') }}</p>
    <div class="verify-email-stack">
      <p
        v-if="!changeEmailOpen"
        class="verify-email-addr"
      >
        <strong>{{ email }}</strong>
      </p>

      <template v-if="!changeEmailOpen">
        <button
          type="button"
          class="sb-next"
          :disabled="resendStatus === 'sending'"
          @click="resendVerificationEmail"
        >
          <template v-if="resendStatus === 'sending'">
            <span
              class="spinner"
              aria-hidden="true"
            ></span>
            {{ t('verify_email_resend_sending') }}
          </template>
          <template v-else>
            {{ t('verify_email_resend') }}
          </template>
        </button>

        <button
          type="button"
          class="sb-back verify-email-change-trigger"
          @click="openChangeEmail"
        >
          {{ t('verify_email_change') }}
        </button>
      </template>

      <template v-else>
        <div class="signup-field">
          <label
            class="field-label"
            id="verify-change-email-label"
            >{{ t('verify_email_change_label') }}</label
          >
          <input
            id="verify-change-email"
            v-model="changeEmailDraft"
            type="email"
            class="signup-input"
            aria-labelledby="verify-change-email-label"
            :placeholder="t('ph_email')"
            inputmode="email"
            autocomplete="email"
          />
          <p
            v-if="changeEmailError"
            class="err"
          >
            <span>{{ changeEmailError }}</span>
          </p>
        </div>

        <button
          type="button"
          class="sb-next"
          :disabled="changeEmailStatus === 'sending'"
          @click="submitChangeEmail"
        >
          <template v-if="changeEmailStatus === 'sending'">
            <span
              class="spinner"
              aria-hidden="true"
            ></span>
            {{ t('verify_email_change_sending') }}
          </template>
          <template v-else>
            {{ t('verify_email_change_submit') }}
          </template>
        </button>

        <button
          type="button"
          class="sb-back verify-email-change-trigger"
          @click="cancelChangeEmail"
        >
          {{ t('verify_email_change_cancel') }}
        </button>
      </template>

      <p
        v-if="changeEmailDone"
        class="field-note"
      >
        {{ t('verify_email_change_done') }}
      </p>
      <p
        v-else-if="resendStatus === 'sent'"
        class="field-note"
      >
        {{ t('verify_email_resend_done') }}
      </p>
      <p
        v-else-if="resendStatus === 'error'"
        class="err"
      >
        {{ t('verify_email_resend_err') }}
      </p>

      <p class="field-note">{{ t('verify_email_spam_hint') }}</p>
    </div>
  </div>
</template>

<script>
import { i18nMixin, refreshCsrfToken, EMAIL_RE } from '../helpers';

export default {
  name: 'VerifyEmailStep',
  mixins: [i18nMixin],
  props: {
    email: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      resendStatus: '',
      changeEmailOpen: false,
      changeEmailDraft: '',
      changeEmailStatus: '',
      changeEmailError: '',
      changeEmailDone: false,
    };
  },
  methods: {
    openChangeEmail() {
      this.changeEmailDraft = this.email;
      this.changeEmailOpen = true;
      this.changeEmailError = '';
      this.changeEmailDone = false;
      this.resendStatus = '';
    },
    cancelChangeEmail() {
      this.changeEmailOpen = false;
      this.changeEmailError = '';
      this.changeEmailStatus = '';
    },
    async submitChangeEmail() {
      if (this.changeEmailStatus === 'sending') {
        return;
      }
      const nextEmail = (this.changeEmailDraft || '').trim();
      if (!EMAIL_RE.test(nextEmail)) {
        this.changeEmailError = this.t('verify_email_change_invalid');
        return;
      }
      this.changeEmailError = '';
      this.changeEmailStatus = 'sending';
      try {
        await refreshCsrfToken();
        await axios.post(zRoute('register.email.update'), { email: nextEmail });
        this.$emit('email-changed', nextEmail);
        this.changeEmailOpen = false;
        this.changeEmailStatus = '';
        this.changeEmailDone = true;
        this.resendStatus = '';
      } catch (e) {
        this.changeEmailStatus = '';
        const data = e && e.response && e.response.data;
        const emailErr = data && data.errors && data.errors.email && data.errors.email[0];
        this.changeEmailError =
          emailErr || (data && data.message) || this.t('verify_email_change_err');
      }
    },
    async resendVerificationEmail() {
      if (this.resendStatus === 'sending') {
        return;
      }
      this.resendStatus = 'sending';
      this.changeEmailDone = false;
      try {
        await refreshCsrfToken();
        await axios.post(zRoute('verification.email.resend'));
        this.resendStatus = 'sent';
      } catch (e) {
        this.resendStatus = 'error';
      }
    },
  },
};
</script>

<style scoped>
.verify-email-stack {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
}

.verify-email-change-trigger {
  align-self: center;
  padding-top: 0;
}

.sb-next {
  width: 100%;
  justify-content: center;
}
</style>
